import React, { useRef, useState, useCallback, useEffect } from "react";
import { Button, Col, Drawer, Form, InputNumber, Modal, Row, Input } from "antd";
import { changeQuote } from "@/services/quotes_new";
import useScreenDetector from "@/hooks/useScreenDetector";
import MarginFeedbackAlert from "@/components/MarginFeedbackAlert";

const { TextArea } = Input;

function EditQuoteDrawer({ visible, quoteData, onNotesUpdate, onClose: propOnClose, onQuoteUpdate, shipmentId }) {
  const quotesEditFormRef = useRef();
  const lowPriceMarginFormRef = useRef();

  const { isMobile } = useScreenDetector();

  const [submitting, setSubmitting] = useState(false);
  const [dealerPrice, setDealerPrice] = useState(0);
  const [transporterPrice, setTransporterPrice] = useState(0);
  const [displayLowPriceMarginModal, setDisplayLowPriceMarginModal] = useState(false);

  const onClose = useCallback(() => {
    setDisplayLowPriceMarginModal(false);
    setSubmitting(false);
    quotesEditFormRef.current?.resetFields();
    lowPriceMarginFormRef.current?.resetFields();

    if (typeof propOnClose === "function") {
      propOnClose();
    }
  }, [propOnClose]);

  const fillFormFields = useCallback(() => {
    const { id: quoteId, dealerPrice: currentDealerPrice, transporterPrice: currentTransporterPrice } = quoteData;
    setDealerPrice(currentDealerPrice);
    setTransporterPrice(currentTransporterPrice);

    quotesEditFormRef.current.setFieldsValue({
      quoteId,
      dealerPrice: currentDealerPrice,
      transporterPrice: currentTransporterPrice,
    });
  }, [quoteData]);

  const onSubmit = useCallback(
    validated => {
      quotesEditFormRef.current.validateFields().then(async result => {
        const { dealerPrice: currentDealerPrice, transporterPrice: currentTransporterPrice, quoteId } = result;
        const margin = Math.abs((currentTransporterPrice - currentDealerPrice) / currentDealerPrice) * 100;
        const isLowMargin = margin < 15 || currentTransporterPrice >= currentDealerPrice;

        let lowPriceMarginResult = null;

        if (isLowMargin && !validated) {
          setDisplayLowPriceMarginModal(true);
          return;
        }
        if (isLowMargin) {
          try {
            lowPriceMarginResult = await lowPriceMarginFormRef.current.validateFields();
          } catch {
            return;
          }
        }

        const payload = {
          dealerPrice: currentDealerPrice,
          transporterPrice: currentTransporterPrice,
          lowMarginReason: lowPriceMarginResult?.lowMarginReason,
          shipmentId,
        };

        setSubmitting(true);
        changeQuote(quoteId, payload)
          .then(response => {
            onNotesUpdate?.();

            onQuoteUpdate?.(response);

            onClose();
          })
          .finally(() => {
            setSubmitting(false);
          });
      });
    },
    [onNotesUpdate, onQuoteUpdate, onClose, shipmentId],
  );

  useEffect(() => {
    if (visible) {
      fillFormFields();
    }
  }, [visible, fillFormFields]);

  return (
    <>
      <Drawer
        title="Change quote"
        width={isMobile ? "80%" : "40%"}
        onClose={onClose}
        open={visible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Row align="space-between">
            <Col flex={1}>
              <MarginFeedbackAlert dealerPrice={Number(dealerPrice)} transporterPrice={Number(transporterPrice)} />
            </Col>
            <Col flex={3}>
              <div style={{ textAlign: "right" }}>
                <Button onClick={onClose} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" onClick={() => onSubmit(false)} loading={submitting}>
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        }
      >
        <Form ref={quotesEditFormRef} layout="vertical">
          <Form.Item name="dealerPrice" label="Dealer Price">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={value => value.replace(/\$\s?|(,*)/g, "")}
              onChange={setDealerPrice}
            />
          </Form.Item>
          <Form.Item name="transporterPrice" label="Transporter Price">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={value => value.replace(/\$\s?|(,*)/g, "")}
              onChange={setTransporterPrice}
            />
          </Form.Item>
          <Form.Item name="quoteId">
            <InputNumber style={{ display: "none" }} />
          </Form.Item>
        </Form>
      </Drawer>
      <Modal
        title="Low Pricing Margin"
        open={displayLowPriceMarginModal}
        onOk={() => onSubmit(true)}
        onCancel={() => setDisplayLowPriceMarginModal(false)}
        confirmLoading={submitting}
        afterClose={() => {
          lowPriceMarginFormRef?.current.resetFields();
        }}
      >
        <div style={{ marginBottom: 32 }}>Pricing margin is less than 15%. Please enter reason for low margin.</div>
        <Form ref={lowPriceMarginFormRef} layout="vertical">
          <Form.Item name="lowMarginReason" label="Reason for Low Margin" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default EditQuoteDrawer;
